import React from 'react';
import { Link } from '@oysterjs/ui/link';
import { TableHeader } from '@oysterjs/ui/table';
import { type ColumnDef } from '@oysterjs/ui/data-table';
import { Checkbox } from '@oysterjs/ui/checkbox';
import { Badge } from '@oysterjs/ui/badge';
import type { CustomPresetFieldsFragment } from '~/api/graphql';
import { VisibilityBadge } from '~/components/visibility/visibility-badge';
import {
  canEditPreset,
  canSelectPreset,
  isPresetReady,
  selectablePresetIds,
  sharedAccessBadgeLabel
} from './presets.utils';
import { CustomPresetState } from '~/api/graphql';

export type PresetSortKey = 'title' | 'user' | 'date';
export type PresetGroupKey = never;

const uniq = <T,>(arr: T[]) => [...new Set(arr)];

const SharedAccessBadge = ({ preset }: { preset: CustomPresetFieldsFragment }) => {
  const label = sharedAccessBadgeLabel(preset);
  return label ? <Badge color="sky">{label}</Badge> : null;
};

/**
 * Says why a row is inert, or that a finished row still wants a look. Without it
 * a generating preset is indistinguishable from a finished one: it is titled
 * after its source workspace until the drafted title lands, so nothing else on
 * the row marks it as unfinished.
 */
const StateBadge = ({ preset }: { preset: CustomPresetFieldsFragment }) => {
  switch (preset.state) {
    case CustomPresetState.Generating:
      return <Badge color="sky">Generating</Badge>;
    case CustomPresetState.Failed:
      return <Badge color="red">Failed</Badge>;
    case CustomPresetState.NeedsReview:
      return <Badge color="amber">Needs review</Badge>;
    default:
      return null;
  }
};

export function getPresetColumns({
  selectedSet,
  setSelectedPresets,
  filtered,
  canRun,
  canEdit,
  canShare,
  onShare
}: {
  selectedSet: ReadonlySet<string>;
  setSelectedPresets: React.Dispatch<React.SetStateAction<string[]>>;
  filtered: CustomPresetFieldsFragment[];
  /** Whether the caller may start a workspace, which is what running a preset does. */
  canRun: boolean;
  /** Whether the caller holds CUSTOM_PRESET:UPDATE. */
  canEdit: boolean;
  /**
   * Whether the Share row action is shown (owner + CUSTOM_PRESET:SHARE + the
   * agency has a federated child agency to share to).
   */
  canShare: (preset: CustomPresetFieldsFragment) => boolean;
  onShare: (preset: CustomPresetFieldsFragment) => void;
}): ColumnDef<CustomPresetFieldsFragment, PresetSortKey, PresetGroupKey>[] {
  return [
    {
      id: 'select',
      noHref: true,
      cellClassName: 'w-0 py-1.5 px-3',
      renderHeader: () => {
        // Only owned presets can be bulk-selected; shared (read/run-only) presets
        // are excluded from "select all" so they can never enter bulk actions.
        const selectable = selectablePresetIds(filtered);
        if (selectable.length === 0) {
          return <TableHeader className="w-0 py-1.5 px-3" />;
        }
        return (
          <TableHeader className="w-0 py-1.5 px-3 text-xs">
            <Checkbox
              aria-label="Select all presets"
              checked={selectable.every((id) => selectedSet.has(id))}
              color="sky"
              onChange={(v) => {
                setSelectedPresets((prev) => (v ? uniq([...prev, ...selectable]) : []));
              }}
            />
          </TableHeader>
        );
      },
      renderCell: (preset) =>
        canSelectPreset(preset) ? (
          <Checkbox
            aria-label={`Select ${preset.title}`}
            checked={selectedSet.has(preset.id)}
            color="sky"
            onChange={(v) => {
              setSelectedPresets((prev) =>
                v ? uniq([...prev, preset.id]) : prev.filter((id) => id !== preset.id)
              );
            }}
          />
        ) : null
    },
    {
      id: 'title',
      header: 'Title',
      sortKey: 'title',
      renderCell: (preset) => (
        <div className="min-w-0">
          <span
            className="flex items-center gap-1.5 truncate text-sm font-medium text-neutral-900 dark:text-white"
            title={preset.title}
          >
            <span className="truncate">{preset.title}</span>
            <VisibilityBadge visibility={preset.visibility} variant="list" />
            <SharedAccessBadge preset={preset} />
            <StateBadge preset={preset} />
          </span>
        </div>
      )
    },
    {
      id: 'user',
      header: 'Created By',
      sortKey: 'user',
      renderCell: (preset) => (
        <span
          className="block truncate text-sm text-neutral-600 dark:text-neutral-400"
          title={preset.createdBy?.name}
        >
          {preset.createdBy?.name}
        </span>
      )
    },
    {
      id: 'date',
      header: 'Last Updated',
      sortKey: 'date',
      renderCell: (preset) => (
        <span className="block truncate text-xs text-neutral-600 dark:text-neutral-400">
          {preset.updatedAt &&
            new Intl.DateTimeFormat('en-US', {
              dateStyle: 'medium',
              timeStyle: 'short'
            }).format(new Date(preset.updatedAt))}
        </span>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      noHref: true,
      cellClassName: 'w-0 py-1.5 px-3',
      renderCell: (preset) => {
        // Run, Edit and Share are the other three doors into a preset the server
        // refuses while it is not ready (createCaraWorkspace, updateCustomPreset
        // and ShareCustomPreset each reject it), so they close with the row link.
        const ready = isPresetReady(preset);
        return (
          <div className="flex flex-row gap-4">
            {ready && canRun && (
              <Link
                href={`/app/presets/${preset.id}/run`}
                onClick={(e) => e.stopPropagation()}
                className="font-medium text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Run
              </Link>
            )}
            {ready && canEdit && canEditPreset(preset) && (
              <Link
                href={`/app/presets/${preset.id}/edit`}
                onClick={(e) => e.stopPropagation()}
                className="font-medium text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Edit
              </Link>
            )}
            {ready && canShare(preset) && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onShare(preset);
                }}
                className="font-medium text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Share
              </button>
            )}
          </div>
        );
      }
    }
  ];
}

/** sortFn for DataTable: sorts by sortKey and sortDirection. */
export function presetSortFn(
  data: CustomPresetFieldsFragment[],
  sortKey: PresetSortKey,
  direction: 'asc' | 'desc'
): CustomPresetFieldsFragment[] {
  const sorted = [...data].sort((a, b) => {
    if (sortKey === 'title') {
      return (a.title || '').localeCompare(b.title || '');
    }
    if (sortKey === 'user') {
      return (a.createdBy?.name ?? '').localeCompare(b.createdBy?.name ?? '');
    }
    // date
    const aDate = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const bDate = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return aDate - bDate;
  });
  const reverse =
    (sortKey === 'date' && direction === 'asc') || (sortKey !== 'date' && direction === 'desc');
  return reverse ? [...sorted].reverse() : sorted;
}
